import React, { useMemo, useState, useEffect } from "react";
import {
  makeStyles,
  tokens,
  Tooltip,
  Spinner,
  Button,
  Text,
  CounterBadge,
  Toaster,
  useToastController
} from "@fluentui/react-components";
import {
  PeopleRegular, CloudRegular, ArrowUp16Regular,
  ArrowDown16Regular,
  ArrowSort16Regular,
} from "@fluentui/react-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  getSortedRowModel
} from "@tanstack/react-table";
import { useCustomers } from "./useCustomers";
import { rankItem } from "@tanstack/match-sorter-utils";
import { formatDateTime } from "../adminUtil";
import GlobalTableSearch from "./GlobalTableSearch";
import AdminTopbar from "../AdminTopbar";
import EditAcsDialog from "./EditAcsDialog";

const useStyles = makeStyles({
  container: {
    padding: "8px 6px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: tokens.colorNeutralBackground1,

    "& th, & td": {
      padding: "6px 6px",
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      textAlign: "left",
    },

    "& thead th": {
      background: tokens.colorNeutralBackground2,
      fontWeight: 600,
    },
  },
  actions: {
    display: "flex",
    gap: "6px",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    flexDirection: "column",
    gap: "10px",
  },
  topbar: {
    gridColumn: "2",
    gridRow: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: 'space-between',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    position: "sticky",
    top: '0px'
  },
  globalSearch: {
    marginRight: "24px"
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "999px",
    fontSize: "12px",
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForegroundLink,
  },
  syncColumn: {
    maxWidth: "140px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
});

const columnHelper = createColumnHelper();

export default function CustomersTable({ onNavigate }) {
  const { data, loading, error, refetch } = useCustomers();
  const [sorting, setSorting] = useState([]);
  const styles = useStyles();
  const toasterId = "global-toaster-customers";
  const { dispatchToast } = useToastController(toasterId);
  const columns = useMemo(
    () => [
      columnHelper.accessor("tenantName", {
        header: "Tenant Name",
        cell: (info) => <strong>{info.getValue()}</strong>,
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("tenantId", {
        header: "Tenant ID",
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("clientAppApproverUser", {
        header: "Client App Approver",
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("tenantOnboardingDate", {
        header: "Onboarding Date (UTC)",
        cell: (info) => formatDateTime(info.getValue()),
        sortingFn: (rowA, rowB, columnId) => {
          return new Date(rowA.getValue(columnId) || 0) -
            new Date(rowB.getValue(columnId) || 0);
        },
      }),
      columnHelper.accessor("syncAppApproveDate", {
        header: "Sync App Date (UTC)",
        cell: (info) => formatDateTime(info.getValue()),
        sortingFn: (rowA, rowB, columnId) => {
          return new Date(rowA.getValue(columnId) || 0) -
            new Date(rowB.getValue(columnId) || 0);
        },
      }),
      columnHelper.accessor("syncAppApproverUser", {
        header: "Sync App Approver",
        sortingFn: "alphanumeric",
        cell: (info) => (
          <div className={styles.syncColumn}>
            {info.getValue()}
          </div>),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Tooltip content={"View Users"} relationship="label">
              <Button
                size="small"
                appearance="transparent"
                icon={<PeopleRegular />}
                onClick={() => onNavigate?.("users", row.original)}
              />
            </Tooltip>
            <Tooltip content={"View Salesforce Orgs"} relationship="label">
              <Button
                size="small"
                appearance="transparent"
                icon={<CloudRegular />}
                onClick={() => onNavigate?.("salesforce", row.original)}
              />
            </Tooltip>
            <EditAcsDialog
              dispatchToast={dispatchToast}
              row={row.original}
              onSuccess={() => {
                //  refresh customers data
                refetch();
              }}
            />
          </div>
        ),
      }),
    ],
    [onNavigate],
  );

  // Global filter state
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState(globalFilter);

  // Debounce global filter for performance
  useEffect(() => {
    // eslint-disable-next-line no-undef
    const handler = setTimeout(() => setDebouncedFilter(globalFilter), 200);
    // eslint-disable-next-line no-undef
    return () => clearTimeout(handler);
  }, [globalFilter]);

  // Custom global filter function (case-insensitive, string and formatted date support)
  function fuzzyFilter(row, columnId, value, addMeta) {
    // Get raw value or formatted date string
    let rowValue = row.getValue(columnId);
    if (typeof rowValue === "undefined" || rowValue === null) return false;
    // Format date columns for search
    if (
      columnId === "tenantOnboardingDate" ||
      columnId === "syncAppApproveDate"
    ) {
      rowValue = formatDateTime(rowValue);
    }
    // Always compare as string, case-insensitive
    const itemRank = rankItem(String(rowValue), value, {
      threshold: 0.1,
      keepDiacritics: false,
    });
    addMeta(itemRank);
    return itemRank.passed;
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: fuzzyFilter,
    state: {
      globalFilter: debouncedFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    enableMultiSort: true,
  });
  //  Loading UI
  if (loading) {
    return (
      <>
        <Toaster toasterId={toasterId} />
        <div className={styles.center}>
          <Spinner size="large" />
          <Text>Loading customers...</Text>
        </div>
      </>
    );
  }

  //  Error UI
  if (error) {
    return (
      <>
        <Toaster toasterId={toasterId} />
        <div className={styles.center}>
          <Text>Failed to load customers ❌</Text>
        </div>
      </>
    );
  }

  const filteredRows = table.getRowModel().rows;
  // Calculate customer count (filtered if searching, otherwise all)
  const customerCount = loading ? null : data?.length || 0;

  return (
    <>
      <Toaster toasterId={toasterId} />
      <header className={styles.topbar}>
        <AdminTopbar title={"Manage Customers"} titleExtra={
          <Tooltip content="Total customers" relationship="label">
            {loading ? (
              <Spinner size="tiny" style={{ marginLeft: 4 }} />
            ) : (
              <CounterBadge className={styles.badge} count={customerCount} />
            )}
          </Tooltip>
        } />
        <div className={styles.globalSearch}>
          <GlobalTableSearch
            value={globalFilter}
            onChange={setGlobalFilter}
            placeholder="Search customers..."
          />
        </div>
      </header>
      <div className={styles.container}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={(e) =>
                      header.column.getToggleSortingHandler()?.(e)
                    }
                    style={{
                      cursor: header.column.getCanSort() ? "pointer" : "default",
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {header.column.getCanSort() && (
                        <Tooltip
                          content={
                            header.column.getIsSorted()
                              ? `Sorted ${header.column.getIsSorted() === "asc"
                                ? "ascending"
                                : "descending"
                              }. Hold Shift to multi-sort.`
                              : "Click to sort. Hold Shift to multi-sort."
                          }
                          relationship="label"
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              marginLeft: 4,
                            }}
                          >
                            {header.column.getIsSorted() === "asc" && (
                              <ArrowUp16Regular />
                            )}

                            {header.column.getIsSorted() === "desc" && (
                              <ArrowDown16Regular />
                            )}

                            {!header.column.getIsSorted() && (
                              <ArrowSort16Regular />
                            )}

                            {/* Multi-sort index */}
                            {header.column.getIsSorted() && (
                              <span style={{ fontSize: 10, marginLeft: 2 }}>
                                {header.column.getSortIndex() + 1}
                              </span>
                            )}
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {filteredRows.length > 0 ? (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ padding: "16px" }}>
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>

  );
}
